import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import EmployerReportReasonForm from '../../components/Forms/EmployerReportReasonForm';
import ConfirmDialog from '../../components/ConfirmDialog';
import LogsAction from '../../components/LogsAction';
import employerReportReasonsApi from '../../api/masters/employerReportReasonsApi';
import { getSidebarState, saveSidebarState, saveScrollPosition, getScrollPosition } from '../../utils/stateManager';
import { hasPermission, PERMISSIONS } from '../../utils/permissions';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import './MasterPage.css';

const DEFAULTS = { english_name: 150, hindi_name: 150, sequence: 80, status: 80, actions: 90 };

export default function EmployerReportReasons() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirm, setConfirm] = useState({ open: false, id: null, title: '', message: '' });
  const [message, setMessage] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const canViewMasters = hasPermission(PERMISSIONS.MASTERS_VIEW);
  const canManageMasters = hasPermission(PERMISSIONS.MASTERS_MANAGE);
  const canDeleteMasters = hasPermission(PERMISSIONS.MASTERS_DELETE);
  const navigate = useNavigate();
  const { colWidths, rHandle } = useResizableColumns('employer-report-reasons-col-widths', DEFAULTS);

  useEffect(() => {
    setSidebarOpen(getSidebarState());
    if (canViewMasters) {
      fetchReasons();
    } else {
      setLoading(false);
    }
  }, [canViewMasters]);

  useEffect(() => {
    if (!showForm) {
      const mainContent = document.querySelector('.main-content');
      if (mainContent) {
        const scrollPos = getScrollPosition('employerreportreasons-scroll');
        mainContent.scrollTop = scrollPos;

        const handleScroll = () => {
          saveScrollPosition('employerreportreasons-scroll', mainContent.scrollTop);
        };
        mainContent.addEventListener('scroll', handleScroll);
        return () => mainContent.removeEventListener('scroll', handleScroll);
      }
    }
  }, [showForm]);

  const fetchReasons = async () => {
    setLoading(true);
    try {
      const response = await employerReportReasonsApi.getAll();
      const sorted = (response.data.data || []).sort((a, b) => {
        const seqA = a.sequence || 0;
        const seqB = b.sequence || 0;
        return seqA - seqB;
      });
      setReasons(sorted);
    } catch (error) {
      console.error('Error fetching report reasons:', error);
      setMessage({ type: 'error', text: 'Failed to fetch report reasons' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin');
    navigate('/login');
  };

  const handleMenuClick = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    saveSidebarState(newState);
  };

  const handleDragStart = (e, index) => {
    if (!canManageMasters) return;
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    if (!canManageMasters) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    if (!canManageMasters) return;
    e.preventDefault();
    if (draggedItem === null || draggedItem === dropIndex) {
      setDraggedItem(null);
      return;
    }

    try {
      const newReasons = [...reasons];
      const draggedReason = newReasons[draggedItem];
      newReasons.splice(draggedItem, 1);
      newReasons.splice(dropIndex, 0, draggedReason);

      const updatedReasons = newReasons.map((reason, idx) => ({
        ...reason,
        sequence: idx + 1,
      }));

      setReasons(updatedReasons);
      setDraggedItem(null);

      await employerReportReasonsApi.updateSequence(updatedReasons);
      setMessage({ type: 'success', text: 'Sequence updated successfully' });
    } catch (error) {
      console.error('Error updating sequence:', error);
      setMessage({ type: 'error', text: 'Failed to update sequence' });
      fetchReasons();
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const confirmDelete = (id) => {
    setConfirm({
      open: true,
      id,
      title: 'Delete Report Reason',
      message: 'Are you sure you want to delete this report reason? This action cannot be undone.',
    });
  };

  const handleDeleteConfirmed = async () => {
    const id = confirm.id;
    setConfirm({ ...confirm, open: false });
    try {
      await employerReportReasonsApi.delete(id);
      setReasons(reasons.filter((r) => r.id !== id));
      setMessage({ type: 'success', text: 'Report Reason deleted successfully' });
    } catch (error) {
      console.error('Error deleting report reason:', error);
      setMessage({ type: 'error', text: 'Failed to delete report reason' });
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingId(null);
    fetchReasons();
  };

  const handleEdit = (reason) => {
    setEditingId(reason.id);
    setShowForm(true);
  };

  return (
    <div className="dashboard-container">
      <Header onMenuClick={handleMenuClick} onLogout={handleLogout} />
      <div className="dashboard-content">
        <Sidebar isOpen={sidebarOpen} />
        <main className="main-content employer-report-reasons-page">
          <div className="content-wrapper">
            {message && (
              <div className={`inline-message ${message.type === 'error' ? 'error' : 'success'}`}>
                {message.text}
                <button className="msg-close" onClick={() => setMessage(null)}>✕</button>
              </div>
            )}

            {!canViewMasters ? (
              <div className="inline-message error">You do not have permission to view masters.</div>
            ) : !showForm ? (
              <>
                <div className="list-header">
                  <h1>Employer Report Reasons Management</h1>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {canManageMasters && (
                      <button
                        className="btn-primary small"
                        onClick={() => {
                          setEditingId(null);
                          setShowForm(true);
                        }}
                      >
                        + Add Reason
                      </button>
                    )}
                    {canViewMasters && (
                      <LogsAction
                        category="employer report reason"
                        title="Employer Report Reason Logs"
                      />
                    )}
                  </div>
                </div>

                <div className="table-container">
                  <table className="data-table draggable col-resizable" style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
                    <thead>
                      <tr>
                        <th className="drag-handle"></th>
                        <th style={{ width: colWidths.english_name }}>English Name{rHandle('english_name')}</th>
                        <th style={{ width: colWidths.hindi_name }}>Hindi Name{rHandle('hindi_name')}</th>
                        <th style={{ width: colWidths.sequence }}>Sequence{rHandle('sequence')}</th>
                        <th style={{ width: colWidths.status }}>Status{rHandle('status')}</th>
                        <th style={{ width: 'auto', whiteSpace: 'nowrap' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="no-data">
                            Loading...
                          </td>
                        </tr>
                      ) : reasons.length > 0 ? (
                        reasons.map((reason, index) => (
                          <tr
                            key={reason.id}
                            draggable={canManageMasters}
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            className={`draggable-row ${draggedItem === index ? 'dragging' : ''}`}
                          >
                            <td className="drag-handle">
                              <span className="drag-icon">⋮⋮</span>
                            </td>
                            <td>{reason.reason_english}</td>
                            <td>{reason.reason_hindi}</td>
                            <td>{reason.sequence || '-'}</td>
                            <td>
                              <span className={`badge ${reason.is_active ? 'active' : 'inactive'}`}>
                                {reason.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              {canManageMasters && (
                                <button
                                  className="btn-small btn-edit"
                                  onClick={() => handleEdit(reason)}
                                >
                                  Edit
                                </button>
                              )}
                              {canDeleteMasters && (
                                <button
                                  className="btn-small btn-delete"
                                  onClick={() => confirmDelete(reason.id)}
                                >
                                  Delete
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="no-data">
                            No report reasons found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <EmployerReportReasonForm
                reasonId={editingId}
                onClose={handleFormClose}
                onSuccess={(msg) => setMessage(msg)}
              />
            )}

            <ConfirmDialog
              open={confirm.open}
              title={confirm.title}
              message={confirm.message}
              onCancel={() => setConfirm({ ...confirm, open: false })}
              onConfirm={handleDeleteConfirmed}
              confirmLabel="Delete"
              cancelLabel="Cancel"
            />
          </div>
        </main>
      </div>
    </div>
  );
}
